import { ArabicField } from '../../components/admin/TranslateButton';
import { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { quotesAPI, radioAPI, announcementsAPI, usersAPI, galleryAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';

// ══════════════════════════════════════════════════════════════
// ADMIN QUOTES PAGE
// ══════════════════════════════════════════════════════════════
const EMPTY_QUOTE = {
  title: '',
  arabic_text: '',
  translation: '',
  murshid_name: 'Maolana Sayyid Musa Aloba',
  quote_date: '',
  hijri_date: '',
  is_featured: false,
};

export function AdminQuotesPage() {
  const [quotes, setQuotes]     = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_QUOTE);
  const [saving, setSaving]     = useState(false);

  const fetchQuotes = () => quotesAPI.getAll().then(r => setQuotes(r.data.data || []));
  useEffect(() => { fetchQuotes(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_QUOTE);
    setShowForm(true);
  };

  const openEdit = (q) => {
    setEditId(q.id);
    setForm({
      title:        q.title        || '',
      arabic_text:  q.arabic_text  || '',
      translation:  q.translation  || '',
      murshid_name: q.murshid_name || 'Maolana Sayyid Musa Aloba',
      quote_date:   q.quote_date   ? q.quote_date.split('T')[0] : '',
      hijri_date:   q.hijri_date   || '',
      is_featured:  !!q.is_featured,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_QUOTE); };

  const saveQuote = async (e) => {
    e.preventDefault();
    if (!form.translation && !form.arabic_text) {
      toast.error('Please enter at least the quote text in English or Arabic.');
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await quotesAPI.update(editId, form);
        toast.success('Quote updated successfully!');
      } else {
        await quotesAPI.create(form);
        toast.success('Quote saved successfully!');
      }
      closeForm();
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save quote.');
    } finally { setSaving(false); }
  };

  const toggleFeatured = async (q) => {
    try {
      await quotesAPI.update(q.id, { is_featured: !q.is_featured });
      toast.success(q.is_featured ? 'Removed from featured.' : 'Marked as featured!');
      fetchQuotes();
    } catch {
      toast.error('Failed to update featured status.');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this quote? This cannot be undone.')) return;
    try {
      await quotesAPI.delete(id);
      toast.success('Quote deleted.');
      fetchQuotes();
    } catch {
      toast.error('Failed to delete quote.');
    }
  };

  // Format a Gregorian date for display
  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">Murshid Quotes</h1>
        <button onClick={openCreate} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> New Quote
        </button>
      </div>

      {/* ── Form ──────────────────────────────────────── */}
      {showForm && (
        <form onSubmit={saveQuote} className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 space-y-4 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold text-navy-700">{editId ? 'Edit Quote' : 'New Quote'}</h2>
            <button type="button" onClick={closeForm} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Title */}
          <div>
            <label className="form-label">
              Quote Title *
              <span className="text-gray-400 font-normal text-xs ml-2">(short heading, e.g. "On Patience", "On Love of Allah")</span>
            </label>
            <input
              required
              className="form-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="e.g. On the Love of Allah"
            />
          </div>

          {/* Dates row */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Date (Gregorian)
                <span className="text-gray-400 font-normal text-xs ml-2">(when it was said/delivered)</span>
              </label>
              <input
                type="date"
                className="form-input"
                value={form.quote_date}
                onChange={e => setForm(f => ({ ...f, quote_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">
                Date (Hijri)
                <span className="text-gray-400 font-normal text-xs ml-2">(optional Islamic calendar date)</span>
              </label>
              <input
                className="form-input"
                value={form.hijri_date}
                onChange={e => setForm(f => ({ ...f, hijri_date: e.target.value }))}
                placeholder="e.g. 15 Ramadan 1445"
              />
            </div>
          </div>

          {/* English quote text */}
          <div>
            <label className="form-label">
              Quote Text (English)
              <span className="text-gray-400 font-normal text-xs ml-2">(primary visible content)</span>
            </label>
            <textarea
              rows={5}
              className="form-input resize-none"
              value={form.translation}
              onChange={e => setForm(f => ({ ...f, translation: e.target.value }))}
              placeholder="e.g. The heart that is filled with the love of Allah finds peace in all circumstances..."
            />
          </div>

          {/* Arabic text */}
          <div>
            <label className="form-label">
              Original Arabic Text
              <span className="text-gray-400 font-normal text-xs ml-2">(optional — displayed above English)</span>
            </label>
            <textarea
              rows={3}
              className="form-input resize-none"
              style={{ fontFamily: "'Amiri','Traditional Arabic',serif", direction: 'rtl', fontSize: '16px', lineHeight: '2', textAlign: 'right' }}
              value={form.arabic_text}
              onChange={e => setForm(f => ({ ...f, arabic_text: e.target.value }))}
              placeholder="النص العربي الأصلي (اختياري)..."
              dir="rtl"
            />
          </div>

          {/* Murshid Name */}
          <div>
            <label className="form-label">Murshid Name</label>
            <input className="form-input" value={form.murshid_name} onChange={e => setForm(f => ({ ...f, murshid_name: e.target.value }))} />
          </div>

          {/* Featured */}
          <div className="flex items-center gap-3">
            <input type="checkbox" id="qfeat" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="h-4 w-4 accent-cyan-500" />
            <label htmlFor="qfeat" className="text-sm text-navy-700">Featured (shows on homepage rotator)</label>
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
              {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving...' : editId ? 'Update Quote' : 'Save Quote'}
            </button>
            <button type="button" onClick={closeForm} className="btn-outline text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      )}

      {/* ── List ──────────────────────────────────────── */}
      <div className="space-y-4">
        {quotes.map(q => (
          <div key={q.id} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex-1 min-w-0">

              {/* Title */}
              {q.title && (
                <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-1">{q.title}</p>
              )}

              {/* Arabic */}
              {q.arabic_text && (
                <p className="arabic text-navy-600 text-base leading-loose mb-1 line-clamp-2">{q.arabic_text}</p>
              )}

              {/* English */}
              <p className="text-navy-800 text-sm font-medium leading-relaxed line-clamp-3">
                {q.translation || '(No English text)'}
              </p>

              {/* Meta: murshid · dates */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                <p className="text-cyan-600 text-xs font-semibold">— {q.murshid_name}</p>
                {(q.quote_date || q.hijri_date) && <span className="text-gray-300 text-xs">·</span>}
                {q.quote_date && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                    <CalendarIcon className="h-3 w-3" />
                    {fmtDate(q.quote_date)}
                  </span>
                )}
                {q.hijri_date && (
                  <span className="text-xs text-gray-400 arabic" style={{ fontFamily: "'Amiri',serif", direction: 'rtl' }}>
                    {q.hijri_date}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 items-end flex-shrink-0">
              <button
                onClick={() => toggleFeatured(q)}
                className={`badge text-xs cursor-pointer transition-all ${q.is_featured ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-gray-100 text-gray-500 hover:bg-amber-50 hover:text-amber-600'}`}
              >
                {q.is_featured ? '⭐ Featured' : 'Set Featured'}
              </button>
              <button onClick={() => openEdit(q)} className="p-1.5 text-gray-300 hover:text-navy-500 hover:bg-navy-50 rounded-lg transition-colors" title="Edit">
                <PencilIcon className="h-4 w-4" />
              </button>
              <button onClick={() => del(q.id)} className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        {quotes.length === 0 && (
          <div className="text-center text-gray-400 py-16">
            <p className="mb-3">No quotes yet.</p>
            <button onClick={openCreate} className="btn-primary text-sm py-2 px-5 inline-flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> Add First Quote
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN RADIO PAGE
// ══════════════════════════════════════════════════════════════
export function AdminRadioPage() {
  const DEFAULTS = { stream_url: '', station_name: 'JNF Radio', description: '', is_live: 1, schedule_info: '' };
  const [form, setForm]     = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    radioAPI.getConfig()
      .then(r => {
        const data = r.data?.data;
        if (data && typeof data === 'object') {
          setForm({ ...DEFAULTS, ...data,
            stream_url:    data.stream_url    || '',
            station_name:  data.station_name  || 'JNF Radio',
            description:   data.description   || '',
            schedule_info: data.schedule_info || '',
            is_live:       data.is_live !== undefined ? data.is_live : 1,
          });
        }
      })
      .catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await radioAPI.updateConfig(form);
      toast.success('Radio configuration updated!');
    } catch {
      toast.error('Failed to save radio config.');
    } finally { setSaving(false); }
  };

  return (
    <AdminLayout>
      <h1 className="text-xl font-bold text-navy-800 mb-6">Radio Configuration</h1>
      <form onSubmit={save} className="max-w-2xl bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        <div><label className="form-label">Stream URL *</label><input required className="form-input" value={form.stream_url} onChange={e => setForm(f => ({ ...f, stream_url: e.target.value }))} placeholder="https://stream.example.com/live" /></div>
        <div><label className="form-label">Station Name</label><input className="form-input" value={form.station_name} onChange={e => setForm(f => ({ ...f, station_name: e.target.value }))} /></div>
        <div><label className="form-label">Description</label><textarea rows={3} className="form-input resize-none" value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
        <div><label className="form-label">Schedule Info</label><textarea rows={4} className="form-input resize-none" value={form.schedule_info || ''} onChange={e => setForm(f => ({ ...f, schedule_info: e.target.value }))} placeholder="e.g. Mon–Fri: 8am–10pm, Sat–Sun: All day" /></div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="live" checked={!!form.is_live} onChange={e => setForm(f => ({ ...f, is_live: e.target.checked ? 1 : 0 }))} className="h-4 w-4 accent-cyan-500" />
          <label htmlFor="live" className="text-sm text-navy-700">Stream is currently live</label>
        </div>
        <button type="submit" disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2">
          {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </form>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN ANNOUNCEMENTS PAGE
// ══════════════════════════════════════════════════════════════
export function AdminAnnouncementsPage() {
  const [items, setItems]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ title: '', title_ar: '', body: '', body_ar: '', is_pinned: false });
  const [saving, setSaving]     = useState(false);

  const fetch = () => announcementsAPI.getAll().then(r => setItems(r.data.data || []));
  useEffect(() => { fetch(); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await announcementsAPI.create(form);
      toast.success('Announcement posted!');
      setShowForm(false);
      setForm({ title: '', title_ar: '', body: '', body_ar: '', is_pinned: false });
      fetch();
    } catch {
      toast.error('Failed to post announcement.');
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await announcementsAPI.delete(id);
      toast.success('Announcement deleted.');
      fetch();
    } catch {
      toast.error('Failed to delete.');
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">Announcements</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> New
        </button>
      </div>
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 space-y-4 shadow-sm">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="form-label">Title (EN) *</label><input required className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <ArabicField label="Title (Arabic)" value={form.title_ar} onChange={v => setForm(f => ({ ...f, title_ar: v }))} sourceText={form.title} />
          </div>
          <div><label className="form-label">Body (EN) *</label><textarea required rows={4} className="form-input resize-none" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} /></div>
          <ArabicField label="Body (Arabic)" value={form.body_ar} onChange={v => setForm(f => ({ ...f, body_ar: v }))} sourceText={form.body} multiline rows={3} />
          <div className="flex items-center gap-3">
            <input type="checkbox" id="pinned" checked={form.is_pinned} onChange={e => setForm(f => ({ ...f, is_pinned: e.target.checked }))} className="h-4 w-4 accent-cyan-500" />
            <label htmlFor="pinned" className="text-sm text-navy-700 font-medium">📌 Pin this announcement</label>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
              {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Posting...' : 'Post'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
          </div>
        </form>
      )}
      <div className="space-y-3">
        {items.map(item => (
          <div key={item.id} className={`bg-white rounded-2xl border p-5 flex gap-4 shadow-sm ${item.is_pinned ? 'border-amber-200' : 'border-gray-100'}`}>
            <div className="flex-1">
              {item.is_pinned && <span className="badge bg-amber-100 text-amber-700 text-xs mb-2 inline-block">📌 Pinned</span>}
              <h3 className="font-semibold text-navy-800">{item.title}</h3>
              {item.title_ar && <p className="arabic text-navy-600 text-sm mt-0.5">{item.title_ar}</p>}
              <p className="text-gray-500 text-sm mt-2 line-clamp-2">{item.body}</p>
              <p className="text-gray-300 text-xs mt-2">{new Date(item.created_at).toLocaleDateString()}</p>
            </div>
            <button onClick={() => del(item.id)} className="p-2 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg flex-shrink-0 transition-colors">
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-gray-400 py-10">No announcements yet.</p>}
      </div>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN USERS PAGE
// ══════════════════════════════════════════════════════════════
export function AdminUsersPage() {
  const [users, setUsers]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ full_name: '', email: '', password: '', role: 'content_admin' });
  const [saving, setSaving]     = useState(false);

  const fetch = () => usersAPI.getAll().then(r => setUsers(r.data.data || []));
  useEffect(() => { fetch(); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await usersAPI.create(form);
      toast.success('User created successfully!');
      setShowForm(false);
      setForm({ full_name: '', email: '', password: '', role: 'content_admin' });
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user.');
    } finally { setSaving(false); }
  };

  const toggle = async (u) => {
    try {
      await usersAPI.update(u.id, { is_active: u.is_active ? 0 : 1 });
      toast.success(u.is_active ? 'User deactivated.' : 'User activated.');
      fetch();
    } catch {
      toast.error('Failed to update user status.');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await usersAPI.delete(id);
      toast.success('User deleted.');
      fetch();
    } catch {
      toast.error('Failed to delete user.');
    }
  };

  const roleColor = (role) => {
    if (role === 'super_admin')   return 'bg-red-100 text-red-700';
    if (role === 'content_admin') return 'bg-blue-100 text-blue-700';
    if (role === 'media_admin')   return 'bg-purple-100 text-purple-700';
    if (role === 'radio_admin')   return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-navy-800">User Management</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> New User
        </button>
      </div>
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 grid md:grid-cols-4 gap-4 items-end shadow-sm">
          <div><label className="form-label">Full Name *</label><input required className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
          <div><label className="form-label">Email *</label><input required type="email" className="form-input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
          <div><label className="form-label">Password *</label><input required type="password" className="form-input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
          <div>
            <label className="form-label">Role</label>
            <select className="form-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              <option value="content_admin">Content Admin</option>
              <option value="media_admin">Media Admin</option>
              <option value="radio_admin">Radio Admin</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <button type="submit" disabled={saving} className="btn-primary text-sm py-3 md:col-span-4 flex items-center justify-center gap-2">
            {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {saving ? 'Creating...' : 'Create User'}
          </button>
        </form>
      )}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>{['Name','Email','Role','Status','Actions'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-navy-800">{u.full_name}</td>
                <td className="px-4 py-3 text-gray-500">{u.email}</td>
                <td className="px-4 py-3"><span className={`badge text-xs capitalize ${roleColor(u.role)}`}>{u.role?.replace('_',' ')}</span></td>
                <td className="px-4 py-3"><span className={`badge text-xs ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => toggle(u)} className="text-xs text-gray-400 hover:text-navy-600 px-2 py-1 rounded hover:bg-gray-100">
                    {u.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button onClick={() => del(u.id)} className="p-1.5 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded transition-colors">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-center text-gray-400 py-10">No admin users yet.</p>}
      </div>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════════════════════
// ADMIN GALLERY PAGE
// ══════════════════════════════════════════════════════════════
export function AdminGalleryPage() {
  const [activeTab, setActiveTab]   = useState('media');
  const [items, setItems]           = useState([]);
  const [albums, setAlbums]         = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [uploadType, setUploadType] = useState('image');
  const [form, setForm]             = useState({ title: '', album_id: '', youtube_url: '' });
  const [file, setFile]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const fileRef = useRef();

  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [albumForm, setAlbumForm]         = useState({ title: '', description: '' });
  const [albumSaving, setAlbumSaving]     = useState(false);
  const [filterAlbum, setFilterAlbum]     = useState('');

  const fetchAll = () => {
    galleryAPI.getItems({ limit: 200 }).then(r => setItems(r.data.data || []));
    galleryAPI.getAlbums().then(r => setAlbums(r.data.data || []));
  };
  useEffect(() => { fetchAll(); }, []);

  const saveMedia = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries({ ...form, file_type: uploadType }).forEach(([k, v]) => v && fd.append(k, v));
      if (file) fd.append('file', file);
      if (uploadType === 'image')       await galleryAPI.uploadImage(fd);
      else if (uploadType === 'audio')  await galleryAPI.uploadAudio(fd);
      else if (uploadType === 'pdf')    await galleryAPI.uploadPDF(fd);
      else if (uploadType === 'video')  await galleryAPI.addVideo({ ...form, file_type: 'video' });
      toast.success('Uploaded successfully!');
      setShowForm(false); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setForm({ title: '', album_id: form.album_id, youtube_url: '' });
      fetchAll();
    } catch { toast.error('Upload failed. Check file size/type and try again.'); }
    finally { setSaving(false); }
  };

  const saveAlbum = async (e) => {
    e.preventDefault();
    if (!albumForm.title.trim()) { toast.error('Album name is required.'); return; }
    setAlbumSaving(true);
    try {
      await galleryAPI.createAlbum(albumForm);
      toast.success(`Album "${albumForm.title}" created!`);
      setShowAlbumForm(false); setAlbumForm({ title: '', description: '' }); fetchAll();
    } catch { toast.error('Failed to create album.'); }
    finally { setAlbumSaving(false); }
  };

  const delItem = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    try { await galleryAPI.deleteItem(id); toast.success('Item deleted.'); fetchAll(); }
    catch { toast.error('Failed to delete item.'); }
  };

  const typeIcon = (t) => ({ image: '🖼', video: '▶', audio: '🎵', pdf: '📄' }[t] || '📁');
  const displayItems = filterAlbum ? items.filter(i => String(i.album_id) === filterAlbum) : items;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-navy-800">Gallery</h1>
        <div className="flex gap-2">
          {activeTab === 'albums' && (
            <button onClick={() => setShowAlbumForm(!showAlbumForm)} className="btn-outline text-sm py-2 px-4 flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> New Album
            </button>
          )}
          {activeTab === 'media' && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
              <PlusIcon className="h-4 w-4" /> Upload
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-100">
        {[{ key: 'media', label: `Media (${items.length})` }, { key: 'albums', label: `Albums (${albums.length})` }].map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-xl border-b-2 transition-all -mb-px ${activeTab === key ? 'border-cyan-400 text-navy-800 bg-white' : 'border-transparent text-gray-400 hover:text-navy-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── ALBUMS TAB ─────────────────────────────────────── */}
      {activeTab === 'albums' && (
        <>
          {showAlbumForm && (
            <form onSubmit={saveAlbum} className="bg-white rounded-2xl border border-gray-100 p-5 mb-5 shadow-sm space-y-4">
              <h2 className="font-semibold text-navy-700">New Album</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Album Name *</label>
                  <input required className="form-input" value={albumForm.title}
                    onChange={e => setAlbumForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Maulid 2024, Ilorin Sawia" />
                </div>
                <div>
                  <label className="form-label">Description <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                  <input className="form-input" value={albumForm.description}
                    onChange={e => setAlbumForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Brief note about this album" />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={albumSaving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
                  {albumSaving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {albumSaving ? 'Creating...' : 'Create Album'}
                </button>
                <button type="button" onClick={() => setShowAlbumForm(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
              </div>
            </form>
          )}
          {albums.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">🗂️</div>
              <p className="mb-3 font-medium">No albums yet. Create one to organise your media.</p>
              <button onClick={() => setShowAlbumForm(true)} className="btn-primary text-sm py-2 px-5 inline-flex items-center gap-1">
                <PlusIcon className="h-4 w-4" /> Create First Album
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {albums.map(a => (
                <div key={a.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy-800">{a.title}</p>
                      {a.description && <p className="text-gray-400 text-xs mt-1 line-clamp-2">{a.description}</p>}
                    </div>
                    <span className="badge bg-cyan-50 text-cyan-600 text-xs flex-shrink-0">{a.item_count || 0} items</span>
                  </div>
                  <button onClick={() => { setActiveTab('media'); setFilterAlbum(String(a.id)); }}
                    className="mt-3 text-xs text-cyan-600 hover:underline font-medium">
                    View items in this album →
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── MEDIA TAB ──────────────────────────────────────── */}
      {activeTab === 'media' && (
        <>
          {showForm && (
            <form onSubmit={saveMedia} className="bg-white rounded-2xl border border-gray-100 p-6 mb-5 space-y-4 shadow-sm">
              <div className="flex gap-2 flex-wrap">
                {['image','video','audio','pdf'].map(t => (
                  <button key={t} type="button" onClick={() => setUploadType(t)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-all border ${uploadType === t ? 'bg-navy-700 text-white border-navy-700' : 'border-gray-200 text-gray-600 hover:border-navy-300'}`}>
                    {typeIcon(t)} {t}
                  </button>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                  <input className="form-input" value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Opening ceremony" />
                </div>
                <div>
                  <label className="form-label">Album <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                  {albums.length === 0 ? (
                    <div className="flex items-center gap-2">
                      <select disabled className="form-input bg-gray-50 text-gray-400 flex-1"><option>No albums yet</option></select>
                      <button type="button" onClick={() => { setActiveTab('albums'); setShowAlbumForm(true); }}
                        className="text-xs text-cyan-600 hover:underline whitespace-nowrap font-medium">+ Create one</button>
                    </div>
                  ) : (
                    <select className="form-input" value={form.album_id} onChange={e => setForm(f => ({ ...f, album_id: e.target.value }))}>
                      <option value="">— No Album —</option>
                      {albums.map(a => <option key={a.id} value={a.id}>{a.title} ({a.item_count || 0} items)</option>)}
                    </select>
                  )}
                </div>
              </div>
              {uploadType === 'video' ? (
                <div>
                  <label className="form-label">YouTube URL *</label>
                  <input required className="form-input" value={form.youtube_url}
                    onChange={e => setForm(f => ({ ...f, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..." />
                </div>
              ) : (
                <div>
                  <label className="form-label">
                    File *
                    <span className="text-gray-400 font-normal text-xs ml-2">
                      {uploadType === 'image' ? 'JPG, PNG, WebP — max 10 MB' :
                       uploadType === 'audio' ? 'MP3, WAV, OGG — max 50 MB' : 'PDF — max 20 MB'}
                    </span>
                  </label>
                  <input ref={fileRef} type="file" required onChange={e => setFile(e.target.files[0])}
                    accept={uploadType === 'image' ? 'image/*' : uploadType === 'audio' ? 'audio/*' : '.pdf'}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-navy-50 file:text-navy-700 hover:file:bg-navy-100"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="btn-primary text-sm py-2 px-5 flex items-center gap-2">
                  {saving && <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {saving ? 'Uploading...' : 'Upload'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-sm py-2 px-5">Cancel</button>
              </div>
            </form>
          )}

          {/* Album filter pills */}
          {albums.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-4">
              <button onClick={() => setFilterAlbum('')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!filterAlbum ? 'bg-navy-700 text-white border-navy-700' : 'border-gray-200 text-gray-600 hover:border-navy-300'}`}>
                All ({items.length})
              </button>
              {albums.map(a => (
                <button key={a.id} onClick={() => setFilterAlbum(String(a.id))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filterAlbum === String(a.id) ? 'bg-navy-700 text-white border-navy-700' : 'border-gray-200 text-gray-600 hover:border-navy-300'}`}>
                  {a.title} ({a.item_count || 0})
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {displayItems.map(item => (
              <div key={item.id} className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-24 bg-navy-100 flex items-center justify-center text-2xl">
                  {item.file_type === 'image' ? <img src={item.file_url} alt="" className="w-full h-full object-cover" /> : typeIcon(item.file_type)}
                </div>
                <div className="p-2">
                  <p className="text-xs text-gray-600 font-medium truncate">{item.title || 'Untitled'}</p>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      item.file_type === 'image' ? 'bg-cyan-50 text-cyan-600' :
                      item.file_type === 'video' ? 'bg-red-50 text-red-600' :
                      item.file_type === 'audio' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
                    }`}>{item.file_type}</span>
                    {item.album_title && <span className="text-gray-300 text-xs truncate">{item.album_title}</span>}
                  </div>
                </div>
                <button onClick={() => delItem(item.id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-lg p-1 transition-opacity">
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
            {displayItems.length === 0 && (
              <p className="col-span-full text-center text-gray-400 py-10">
                {filterAlbum ? 'No media in this album yet.' : 'No media uploaded yet.'}
              </p>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
